import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  CheckCircle, Zap, Shield, Server, Cpu,
  HardDrive, MemoryStick, Loader2, AlertCircle,
  ArrowRight, Star, Lock, GraduationCap, ExternalLink
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import Navbar from '../components/Navbar';
import { Footer } from '../components/Constants';

// ─── sub-components ──────────────────────────────────────────────────────────

function StatusBanner({ status, planName, onManage, managing }) {
  const cfg = {
    active:   { bg: 'bg-green-500/10  border-green-500/30  text-green-400',  label: '● Active' },
    trialing: { bg: 'bg-blue-500/10   border-blue-500/30   text-blue-400',   label: '● Trial' },
    past_due: { bg: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400', label: '⚠ Past Due — update payment method' },
    canceled: { bg: 'bg-red-500/10    border-red-500/30    text-red-400',    label: '✕ Canceled' },
    inactive: { bg: 'bg-slate-700/50  border-slate-600     text-slate-400',  label: 'No active plan' },
  }[status] ?? { bg: 'bg-slate-700/50 border-slate-600 text-slate-400', label: status };

  return (
    <div className={`inline-flex items-center gap-3 px-4 py-2 rounded-full border text-sm font-medium ${cfg.bg}`}>
      <span>{cfg.label}</span>
      {planName && <span className="text-white font-semibold">{planName}</span>}
      {['active','trialing'].includes(status) && (
        <button
          onClick={onManage}
          disabled={managing}
          className="flex items-center gap-1 underline opacity-70 hover:opacity-100 transition-opacity text-xs"
        >
          {managing ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
          Manage billing
        </button>
      )}
    </div>
  );
}

function PlanSpec({ icon, label }) {
  if (!label) return null;
  return (
    <div className="flex items-center gap-1.5 text-xs text-slate-400">
      {icon}
      <span>{label}</span>
    </div>
  );
}
// ─── Student Card ─────────────────────────────────────────────────────────────

function StudentPlanCard() {
  const [requested, setRequested] = useState(false);

  return (
    <div className="relative flex flex-col rounded-2xl border border-purple-500/40 bg-gradient-to-b from-purple-500/10 to-slate-800/60 p-6 transition-all">
      {/* Badge */}
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
        <GraduationCap className="w-3 h-3" /> For Students
      </div>

      {/* Name */}
      <h3 className="text-lg font-bold mb-1">Student Box</h3>
      <p className="text-slate-400 text-xs mb-4 leading-relaxed min-h-[3rem]">
        A free Linux sandbox to learn DevOps, containers, and the command line.
      </p>

      {/* Price */}
      <div className="mb-4">
        <span className="text-3xl font-bold text-purple-400">Free</span>
        <span className="text-slate-400 text-sm ml-1">forever</span>
      </div>

      {/* Specs */}
      <div className="flex flex-wrap gap-2 mb-4">
        <PlanSpec icon={<HardDrive className="w-3 h-3" />} label="1GB storage" />
        <PlanSpec icon={<MemoryStick className="w-3 h-3" />} label="512MB RAM" />
        <PlanSpec icon={<Cpu className="w-3 h-3" />} label="1 shared vCPU" />
      </div>

      {/* Feature notes */}
      <ul className="space-y-1.5 mb-4">
        {[
          '1 Linux box per account',
          'Sleeps after 1hr idle',
          'Expires after 90 days',
          'Community support only',
        ].map((note) => (
          <li key={note} className="flex items-center gap-2 text-xs text-slate-400">
            <CheckCircle className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
            {note}
          </li>
        ))}
      </ul>

      {/* CTA */}
      <div className="mt-auto pt-4">
        {requested ? (
          <div className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold bg-purple-500/10 border border-purple-500/30 text-purple-400">
            <CheckCircle className="w-4 h-4" /> Request sent!
          </div>
        ) : (
          <button
            onClick={() => setRequested(true)}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold bg-purple-500 hover:bg-purple-600 text-white transition-colors"
          >
            <GraduationCap className="w-4 h-4" /> Request Access
          </button>
        )}
        <p className="text-center text-xs text-slate-500 mt-2">
          .edu email or student ID required
        </p>
      </div>
    </div>
  );
}

// ─── main ────────────────────────────────────────────────────────────────────

export default function PricingPage() {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();

  const [plans,          setPlans]          = useState([]);
  const [subscription,   setSubscription]   = useState(null); // current sub
  const [loadingPlans,   setLoadingPlans]   = useState(true);
  const [loadingSub,     setLoadingSub]     = useState(false);
  const [checkoutPlanId, setCheckoutPlanId] = useState(null);
  const [managing,       setManaging]       = useState(false);
  const [error,          setError]          = useState('');

  // Show success/cancel flash from Stripe redirect
  const params      = new URLSearchParams(location.search);
  const checkoutMsg = params.get('checkout');

  useEffect(() => { fetchPlans(); }, []);
  useEffect(() => {
    if (user?.company_id) {
      setLoadingSub(true);
      fetchSubscription().finally(() => setLoadingSub(false));
    }
  }, [user]);

  async function fetchPlans() {
    try {
      const { data } = await api.get('/stripe/plans');
      setPlans(data);
    } catch {
      setError('Failed to load plans. Please refresh.');
    } finally {
      setLoadingPlans(false);
    }
  }

  async function fetchSubscription() {
    try {
      const { data } = await api.get('/stripe/my-subscription');
      setSubscription(data.subscription);
    } catch { /* non-critical */ }
  }

  async function handleSubscribe(planId) {
    setError('');

    // Not logged in → send to login, then back to pricing
    if (!user) {
      navigate(`/login?redirect=/pricing`);
      return;
    }

    if (!['company_admin', 'admin'].includes(user.role)) {
      setError('Only a company admin can subscribe. Ask your company admin to do this.');
      return;
    }

    setCheckoutPlanId(planId);
    try {
      const { data } = await api.post('/stripe/create-checkout-session', { planId });
      window.location.href = data.url;
    } catch (err) {
      setError(err.response?.data?.error ?? 'Failed to start checkout. Please try again.');
      setCheckoutPlanId(null);
    }
  }

  async function handleManageBilling() {
    setError('');
    setManaging(true);
    try {
      const { data } = await api.post('/stripe/create-portal-session');
      window.location.href = data.url;
    } catch (err) {
      setError(err.response?.data?.error ?? 'Failed to open billing portal.');
      setManaging(false);
    }
  }

  const subStatus    = subscription?.stripe_status ?? 'inactive';
  const isSubscribed = ['active', 'trialing'].includes(subStatus);

  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col">

      {/* ── Navbar ── */}
      <Navbar />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <div className="text-center pt-32 pb-12 px-4">
        <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs font-medium px-3 py-1 rounded-full mb-6">
          <Zap className="w-3 h-3" /> Fully managed DevOps
        </div>
        <h1 className="text-5xl font-bold tracking-tight mb-4">
          Simple, transparent pricing
        </h1>
        <p className="text-slate-400 text-lg max-w-lg mx-auto">
          No hidden fees. No infrastructure to manage. Cancel any time.
        </p>

        {/* Checkout flash */}
        {checkoutMsg === 'success' && (
          <div className="mt-6 inline-flex items-center gap-2 bg-green-500/10 border border-green-500/30 text-green-400 px-4 py-2 rounded-lg text-sm">
            <CheckCircle className="w-4 h-4" />
            Payment successful! Your subscription is now active.
          </div>
        )}
        {checkoutMsg === 'canceled' && (
          <div className="mt-6 inline-flex items-center gap-2 bg-slate-700/50 border border-slate-600 text-slate-400 px-4 py-2 rounded-lg text-sm">
            Checkout canceled — no charge was made.
          </div>
        )}

        {/* Current plan status */}
        {user && !loadingSub && subscription && (
          <div className="mt-6 flex justify-center">
            <StatusBanner
              status={subStatus}
              planName={subscription.plan_name}
              onManage={handleManageBilling}
              managing={managing}
            />
          </div>
        )}
      </div>

      {/* ── Error ──────────────────────────────────────────────────────────── */}
      {error && (
        <div className="max-w-lg mx-auto px-4 mb-6">
          <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg px-4 py-3 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            {error}
          </div>
        </div>
      )}

      {/* ── Plan cards ─────────────────────────────────────────────────────── */}
      {loadingPlans ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
        </div>
      ) : (
        <div className="max-w-6xl mx-auto px-4 pb-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <StudentPlanCard />
          {plans.map((plan) => {
            const isCurrentPlan = subscription?.plan_slug === plan.slug;
            const isPopular     = plan.slug === 'namespace'; // most popular
            const isCustom      = plan.is_custom_price;
            const isLoading     = checkoutPlanId === plan.id;
            const anyLoading    = !!checkoutPlanId || managing;

            return (
              <div
                key={plan.id}
                className={`relative flex flex-col rounded-2xl border p-6 transition-all
                  ${isPopular
                    ? 'border-blue-500 bg-gradient-to-b from-blue-500/10 to-slate-800/60 shadow-xl shadow-blue-500/10'
                    : 'border-slate-700 bg-slate-800/50'}
                  ${isCurrentPlan ? 'ring-2 ring-green-500/50' : ''}
                `}
              >
                {/* Badges */}
                {isPopular && !isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    <Star className="w-3 h-3" /> Most Popular
                  </div>
                )}
                {isCurrentPlan && (
                  <div className="absolute -top-3 right-4 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    Your Plan
                  </div>
                )}

                {/* Plan name */}
                <h3 className="text-lg font-bold mb-1">{plan.name}</h3>
                <p className="text-slate-400 text-xs mb-4 leading-relaxed min-h-[3rem]">
                  {plan.description}
                </p>

                {/* Price */}
                <div className="mb-4">
                  {isCustom ? (
                    <div className="text-2xl font-bold text-blue-400">Custom</div>
                  ) : (
                    <>
                      <span className="text-3xl font-bold">
                        ${parseFloat(plan.price_monthly).toFixed(0)}
                      </span>
                      <span className="text-slate-400 text-sm ml-1">/mo</span>
                    </>
                  )}
                </div>

                {/* Specs */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <PlanSpec icon={<HardDrive className="w-3 h-3" />} label={plan.storage_gb ? `${plan.storage_gb}GB storage` : null} />
                  <PlanSpec icon={<MemoryStick className="w-3 h-3" />} label={plan.ram_gb ? `${plan.ram_gb}GB RAM` : null} />
                  <PlanSpec icon={<Cpu className="w-3 h-3" />} label={plan.vcpu ? `${plan.vcpu} vCPU` : null} />
                </div>

                {/* CTA */}
                <div className="mt-auto pt-4">
                  {isCurrentPlan ? (
                    <button
                      onClick={handleManageBilling}
                      disabled={managing}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold bg-green-500/10 border border-green-500/30 text-green-400 hover:bg-green-500/20 transition-colors"
                    >
                      {managing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                      Manage Plan
                    </button>
                  ) : isCustom ? (
                    <a
                      href="/contact"
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold bg-slate-700 hover:bg-slate-600 text-white transition-colors"
                    >
                      Contact Sales <ArrowRight className="w-4 h-4" />
                    </a>
                  ) : !user ? (
                    <button
                      onClick={() => navigate(`/login?redirect=/pricing`)}
                      className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-colors
                        ${isPopular
                          ? 'bg-blue-500 hover:bg-blue-600 text-white'
                          : 'bg-slate-700 hover:bg-slate-600 text-white'}`}
                    >
                      <Lock className="w-4 h-4" /> Login to Subscribe
                    </button>
                  ) : isSubscribed ? (
                    <button
                      onClick={handleManageBilling}
                      disabled={anyLoading}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold bg-slate-700 hover:bg-slate-600 text-white transition-colors disabled:opacity-50"
                    >
                      {managing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      Switch Plan
                    </button>
                  ) : (
                    <button
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={anyLoading}
                      className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50
                        ${isPopular
                          ? 'bg-blue-500 hover:bg-blue-600 text-white'
                          : 'bg-slate-700 hover:bg-slate-600 text-white'}`}
                    >
                      {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                      {isLoading ? 'Redirecting...' : 'Get Started'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Footer note ────────────────────────────────────────────────────── */}
      <div className="border-t border-slate-800 py-10 px-4 text-center">
        <p className="text-slate-500 text-sm max-w-xl mx-auto">
          All plans billed monthly. Upgrade, downgrade, or cancel at any time from your billing portal.
          Need a custom solution?{' '}
          <a href="/contact" className="text-blue-400 hover:underline">Talk to us.</a>
        </p>
      </div>
      
      {/* ── Footer ── */}
      <Footer />

      </div>
  );
}
